import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * User-controlled accessibility preferences (text size, higher contrast, reduced
 * motion). These are NATIVE controls — not a third-party accessibility overlay,
 * which the Malaysian OKU guide warns against (overlays interfere with assistive
 * tech). Persisted to localStorage so the choice survives reloads.
 *
 * The side effects (root font-size, body classes, Framer MotionConfig) live in
 * `A11yProvider`; this store is just the persisted state + setters.
 */
export type TextScale = 100 | 125 | 150;

interface A11yPrefsState {
  textScale: TextScale;
  highContrast: boolean;
  reduceMotion: boolean;
  setTextScale: (value: TextScale) => void;
  setHighContrast: (value: boolean) => void;
  setReduceMotion: (value: boolean) => void;
  reset: () => void;
}

export const useA11yPrefs = create<A11yPrefsState>()(
  persist(
    (set) => ({
      textScale: 100,
      highContrast: false,
      reduceMotion: false,
      setTextScale: (textScale) => set({ textScale }),
      setHighContrast: (highContrast) => set({ highContrast }),
      setReduceMotion: (reduceMotion) => set({ reduceMotion }),
      reset: () => set({ textScale: 100, highContrast: false, reduceMotion: false })
    }),
    { name: "bbcklrr-a11y-prefs" }
  )
);
